import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCompanyLabels } from '../context/CompanyContext';
import { fetchQueries, COMPLAINT_MANAGEMENT_ENDPOINT } from '../redux/complaintmanagement/actions';
import { fetchComplaintSummary, fetchStatuses } from '../redux/status/actions';
import { fetchWorkflowSteps } from '../redux/Workflow/actions';
import { changeComplaintStatus } from '../redux/changestatus/actions';
import { setId } from '../redux/appDetails/reducer';
import { resetComplaintManagementState } from '../redux/complaintmanagement/reducers';
import { resetStatusState } from '../redux/status/reducers';
import { resetChangeStatusState } from '../redux/changestatus/reducers';
import { getSignedUrl } from '../redux/s3/actions';
import { getFormFields } from '../redux/complaint/actions';
import { collectAttachmentPaths, resolveIsAttachmentField } from '../utils/complaintTable';
import api from '../utils/api';
import {
  buildComplaintColumns,
  buildComplaintTableFields,
  buildCsvContent,
  COMPLAINTS_PAGE_SIZE_OPTIONS,
  DEFAULT_COMPLAINTS_PAGE_SIZE,
  formFieldColumnsFromResponse,
  getFirstAvailableComplaintTypeId,
  getPaginationInfo,
  getNextUpdatedAtSortDirection,
  isAttachmentColumn,
  isResolvedOrClosedStatus,
  mapComplaintToTableRow,
  readStoredComplaintTypeId,
  saveComplaintTypeId,
  sortComplaintRowsByUpdatedAt,
  UPDATED_AT_SORT_NEWEST,
  UPDATED_AT_SORT_OLDEST,
  REPORT_ROW_LIMIT,
  STATUS_FIELD,
  ESCALATION_FIELD,
  unwrapComplaintListResponse,
} from '../utils/complaintManagementDetails';

function getStatusDropdownOptions(statusList, row) {
  return statusList.map((item) => {
    const resolveLike = isResolvedOrClosedStatus(item.name);
    const isCurrent = String(item.id) === String(row.statusId);
    let disabled = false;
    if (resolveLike && !row.canResolve) disabled = true;
    if (row.isFinalLevel && !resolveLike) disabled = true;
    if (isCurrent) disabled = false;
    return { value: item.id, label: item.name, disabled };
  }).filter((item) => item.label);
}

function getEscalationDropdownOptions(steps, row) {
  const sorted = [...steps].sort((left, right) => (left.levelNo ?? 0) - (right.levelNo ?? 0));
  const current = sorted.find((step) => String(step.id) === String(row.currentStepId))
    ?? sorted.find((step) => `Level ${step.levelNo}` === row.escalationTo);
  const currentLevel = current?.levelNo;
  const nextLevel = currentLevel != null ? currentLevel + 1 : null;

  return sorted.map((step) => {
    const isCurrent = current != null && String(step.id) === String(current.id);
    const isNext = nextLevel != null && step.levelNo === nextLevel;
    const isFirstWithoutCurrent = current == null && step.levelNo === sorted[0]?.levelNo;
    let disabled = true;
    if (isCurrent) disabled = false;
    else if (!row.isFinalLevel && (isNext || isFirstWithoutCurrent)) disabled = false;
    return {
      value: step.id,
      label: step.levelNo != null ? `Level ${step.levelNo}` : String(step.name || step.id),
      disabled,
    };
  }).filter((item) => item.label);
}

async function fetchAllComplaintsForReport({ complaintTypeId, status }) {
  const response = await api.get(COMPLAINT_MANAGEMENT_ENDPOINT, {
    params: {
      complaintTypeId,
      page: 1,
      limit: REPORT_ROW_LIMIT,
      ...(status != null && status !== '' && status !== 'all' ? { status } : {}),
    },
  });
  return unwrapComplaintListResponse(response.data);
}

export default function useComplaintManagementDetails() {
  const { fieldLabel } = useCompanyLabels();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { complaintTypeId: routeComplaintTypeId } = useParams();
  const [searchParams] = useSearchParams();
  const storeComplaintTypeId = useSelector((store) => store.appDetails?.id);
  const complaintTypeId = getFirstAvailableComplaintTypeId(
    routeComplaintTypeId,
    searchParams.get('complaintTypeId'),
    storeComplaintTypeId,
    readStoredComplaintTypeId(),
  );
  const {
    list = [],
    isLoading = false,
    error = null,
    hasLoaded = false,
    requestedComplaintTypeId = null,
    meta = null,
  } = useSelector((store) => store.complaintManagement ?? {});
  const statusList = useSelector((store) => store.status?.list ?? []);
  const statusUpdateError = useSelector((store) => store.changeStatus?.error ?? null);
  const updatingId = useSelector((store) => store.changeStatus?.updatingId ?? null);
  const workflowSteps = useSelector((store) => store.workflow?.workflowSteps);
  const statusNames = useMemo(() => statusList.map((item) => item.name).filter(Boolean), [statusList]);
  const steps = useMemo(() => (Array.isArray(workflowSteps) ? workflowSteps : []), [workflowSteps]);
  const [statusFilter, setStatusFilter] = useState({ complaintTypeId: null, status: 'all' });
  const [updatedAtSortFilter, setUpdatedAtSortFilter] = useState({ complaintTypeId: null, direction: UPDATED_AT_SORT_NEWEST });
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_COMPLAINTS_PAGE_SIZE);
  const [configuredFieldColumns, setConfiguredFieldColumns] = useState([]);
  const [attachmentUrlMap, setAttachmentUrlMap] = useState({});
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const selectedStatus = String(statusFilter.complaintTypeId) === String(complaintTypeId) ? statusFilter.status : 'all';
  const selectedUpdatedAtSort = String(updatedAtSortFilter.complaintTypeId) === String(complaintTypeId)
    ? updatedAtSortFilter.direction
    : UPDATED_AT_SORT_NEWEST;

  useEffect(() => {
    if (!complaintTypeId || routeComplaintTypeId) return undefined;
    saveComplaintTypeId(complaintTypeId);
    navigate(`/complaintmanagement/${complaintTypeId}`, { replace: true });
    return undefined;
  }, [complaintTypeId, navigate, routeComplaintTypeId]);

  useEffect(() => {
    if (!complaintTypeId || !routeComplaintTypeId) return undefined;
    saveComplaintTypeId(complaintTypeId);
    dispatch(setId(complaintTypeId));
    dispatch(resetComplaintManagementState());
    dispatch(resetStatusState());
    dispatch(resetChangeStatusState());

    const statusesRequest = dispatch(fetchStatuses(complaintTypeId));
    const summaryRequest = dispatch(fetchComplaintSummary(complaintTypeId));
    const workflowRequest = dispatch(fetchWorkflowSteps(complaintTypeId));
    const formFieldsRequest = dispatch(getFormFields(complaintTypeId));
    formFieldsRequest.unwrap()
      .then((response) => setConfiguredFieldColumns(formFieldColumnsFromResponse(response)))
      .catch(() => setConfiguredFieldColumns([]));

    return () => {
      statusesRequest.abort?.();
      summaryRequest.abort?.();
      workflowRequest.abort?.();
      formFieldsRequest.abort?.();
    };
  }, [complaintTypeId, dispatch, routeComplaintTypeId]);

  useEffect(() => {
    if (!complaintTypeId || !routeComplaintTypeId) return undefined;
    const complaintsRequest = dispatch(fetchQueries({
      complaintTypeId,
      page,
      limit: itemsPerPage,
      status: selectedStatus === 'all' ? undefined : selectedStatus,
    }));
    return () => complaintsRequest.abort?.();
  }, [complaintTypeId, dispatch, itemsPerPage, page, routeComplaintTypeId, selectedStatus]);

  const complaintRows = useMemo(() => {
    if (requestedComplaintTypeId != null && complaintTypeId != null
      && String(requestedComplaintTypeId) !== String(complaintTypeId)) return [];
    return list.map((complaint) => ({
      ...mapComplaintToTableRow(complaint, statusList, steps),
      _fields: complaint?.formField || [],
    }));
  }, [complaintTypeId, list, requestedComplaintTypeId, statusList, steps]);
  const complaintColumns = useMemo(
    () => buildComplaintColumns(complaintRows, configuredFieldColumns),
    [complaintRows, configuredFieldColumns],
  );
  const tableFields = buildComplaintTableFields(complaintColumns);
  const sortedComplaintRows = useMemo(
    () => sortComplaintRowsByUpdatedAt(complaintRows, selectedUpdatedAtSort),
    [complaintRows, selectedUpdatedAtSort],
  );
  const tableRows = sortedComplaintRows.map(({ _fields, ...row }) => row);
  const attachmentPaths = useMemo(
    () => collectAttachmentPaths(complaintColumns, sortedComplaintRows),
    [complaintColumns, sortedComplaintRows],
  );
  const attachmentsReady = attachmentPaths.every((path) => Object.prototype.hasOwnProperty.call(attachmentUrlMap, path));

  useEffect(() => {
    const missingPaths = attachmentPaths.filter((path) => !Object.prototype.hasOwnProperty.call(attachmentUrlMap, path));
    if (!missingPaths.length) return undefined;
    let cancelled = false;
    Promise.all(missingPaths.map(async (path) => {
      try {
        const signedUrl = await dispatch(getSignedUrl({ params: { path } })).unwrap();
        return [path, signedUrl];
      } catch {
        return [path, ''];
      }
    })).then((results) => {
      if (!cancelled) setAttachmentUrlMap((previous) => ({ ...previous, ...Object.fromEntries(results) }));
    });
    return () => { cancelled = true; };
  }, [attachmentPaths, attachmentUrlMap, dispatch]);

  const pagination = getPaginationInfo(meta, page, itemsPerPage, tableRows.length);
  const showLoading = Boolean(complaintTypeId && !error && (
    (!tableRows.length && (isLoading || !hasLoaded)) || (tableRows.length > 0 && !attachmentsReady)
  ));

  const handleStatusFilterChange = (nextStatus) => {
    setPage(1);
    setStatusFilter({ complaintTypeId, status: nextStatus });
    if (!complaintTypeId) return;
    dispatch(fetchQueries({ complaintTypeId, page: 1, limit: itemsPerPage, status: nextStatus === 'all' ? undefined : nextStatus }));
  };
  const handlePageChange = (nextPage) => {
    if (nextPage >= 1 && nextPage <= pagination.totalPages && nextPage !== page) setPage(nextPage);
  };
  const handleItemsPerPageChange = (nextItemsPerPage) => {
    const normalized = Number(nextItemsPerPage);
    if (!Number.isFinite(normalized) || normalized <= 0 || normalized === itemsPerPage) return;
    setItemsPerPage(normalized);
    setPage(1);
  };
  const toggleUpdatedAtSortDirection = () => setUpdatedAtSortFilter({
    complaintTypeId,
    direction: getNextUpdatedAtSortDirection(selectedUpdatedAtSort),
  });
  const handleNewComplaint = () => {
    if (complaintTypeId) navigate(`/complaintmanagement/new/${complaintTypeId}`);
  };
  const handleComplaintStatusChange = (row, nextValue) => {
    if (row?.id == null || (updatingId != null && String(updatingId) === String(row.id))) return;
    if (String(nextValue) === String(row.statusId) || String(nextValue) === String(row.status)) return;
    const status = statusList.find((item) => String(item.id) === String(nextValue) || String(item.name) === String(nextValue));
    if (status?.id == null) return;
    const resolveLike = isResolvedOrClosedStatus(status.name);
    if ((resolveLike && !row.canResolve) || (row.isFinalLevel && !resolveLike)) return;
    dispatch(changeComplaintStatus({ id: row.id, statusId: status.id, currentStepId: row.currentStepId, status: status.name }));
  };
  const handleComplaintEscalationChange = (row, nextValue) => {
    if (row?.id == null || (updatingId != null && String(updatingId) === String(row.id))) return;
    if (String(nextValue) === String(row.currentStepId) || String(nextValue) === String(row.escalationTo) || row.isFinalLevel) return;
    const nextStep = steps.find((step) => String(step.id) === String(nextValue) || `Level ${step.levelNo}` === String(nextValue));
    if (nextStep?.id == null) return;
    const current = steps.find((step) => String(step.id) === String(row.currentStepId))
      ?? steps.find((step) => `Level ${step.levelNo}` === row.escalationTo);
    const sortedSteps = [...steps].sort((left, right) => (left.levelNo ?? 0) - (right.levelNo ?? 0));
    if ((current && nextStep.levelNo !== current.levelNo + 1) || (!current && nextStep.levelNo !== sortedSteps[0]?.levelNo)) return;
    dispatch(changeComplaintStatus({ id: row.id, statusId: row.statusId, currentStepId: nextStep.id, status: row.status, level: nextStep.levelNo }));
  };
  const getComplaintCellContext = (row) => ({
    statusOptions: getStatusDropdownOptions(statusList, row),
    escalationOptions: getEscalationDropdownOptions(steps, row),
    onStatusChange: (nextValue) => handleComplaintStatusChange(row, nextValue),
    onEscalationChange: (nextValue) => handleComplaintEscalationChange(row, nextValue),
    attachmentUrlMap,
  });
  const handleDownloadReport = async () => {
    if (!complaintTypeId || isDownloadingReport) return;
    setIsDownloadingReport(true);
    setDownloadError(null);
    try {
      const rawComplaints = await fetchAllComplaintsForReport({ complaintTypeId, status: selectedStatus });
      const reportRows = rawComplaints.map((complaint) => ({
        ...mapComplaintToTableRow(complaint, statusList, steps),
        _fields: complaint?.formField || [],
      }));
      const reportFormColumns = buildComplaintColumns(reportRows, configuredFieldColumns);
      const reportFields = buildComplaintTableFields(reportFormColumns)
        .filter((field) => !isAttachmentColumn(field, reportRows, resolveIsAttachmentField));
      const blob = new Blob([buildCsvContent(reportFields, reportRows)], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `complaints-report-${complaintTypeId}-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (downloadRequestError) {
      setDownloadError(downloadRequestError?.response?.data?.message ?? downloadRequestError?.message ?? 'Failed to download report');
    } finally {
      setIsDownloadingReport(false);
    }
  };

  return {
    complaintTypeId,
    statusNames,
    selectedStatus,
    selectedUpdatedAtSort,
    isDownloadingReport,
    onStatusChange: handleStatusFilterChange,
    onSortChange: toggleUpdatedAtSortDirection,
    onNewComplaint: handleNewComplaint,
    onDownloadReport: handleDownloadReport,
    error,
    downloadError,
    statusUpdateError,
    showLoading,
    tableProps: {
      scrollFields: tableFields.filter((field) => field.key !== 'status' && field.key !== 'escalationTo'),
      stickyFields: [STATUS_FIELD, ESCALATION_FIELD],
      tableRows,
      fieldLabel,
      getCellContext: getComplaintCellContext,
      page,
      itemsPerPage,
    },
    paginationProps: {
      currentPage: page,
      totalItems: pagination.totalItems,
      itemsPerPage,
      onPageChange: handlePageChange,
      onItemsPerPageChange: handleItemsPerPageChange,
      selectOptions: COMPLAINTS_PAGE_SIZE_OPTIONS,
      totalPages: pagination.totalPages,
      disabled: isLoading,
      hasRows: Boolean(pagination.totalItems > 0 || tableRows.length || page > 1),
    },
  };
}
